/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useLocation, useSearch } from 'wouter';

import { PropTypes as MicromagPropTypes } from '@micromag/core';
import { useUrlGenerator, useRoutePush } from '@micromag/core/contexts';

import Menu from './Menu';
import ThemeDeleteModal from '../modals/ThemeDelete';
import ThemeDuplicateModal from '../modals/ThemeDuplicate';

const propTypes = {
    theme: MicromagPropTypes.story.isRequired,
    className: PropTypes.string,
    itemClassName: PropTypes.string,
    linkClassName: PropTypes.string,
    withoutDropdown: PropTypes.bool,
    asDropdown: PropTypes.bool,
    asList: PropTypes.bool,
    asLine: PropTypes.bool,
    flush: PropTypes.bool,
    dropdownAlign: MicromagPropTypes.dropdownAlign,
    withScreens: PropTypes.bool,
    withEditor: PropTypes.bool,
    withDuplicate: PropTypes.bool,
    withDelete: PropTypes.bool,
};

const defaultProps = {
    className: null,
    itemClassName: null,
    linkClassName: null,
    withoutDropdown: false,
    asList: false,
    asLine: false,
    asDropdown: false,
    flush: false,
    dropdownAlign: 'end',
    withScreens: false,
    withEditor: false,
    withDuplicate: false,
    withDelete: false,
};

const ThemeMenu = ({
    theme,
    className,
    itemClassName,
    linkClassName,
    withoutDropdown,
    asList,
    asLine,
    asDropdown,
    flush,
    dropdownAlign,
    withScreens,
    withEditor,
    withDuplicate,
    withDelete,
    ...props
}) => {
    const url = useUrlGenerator();
    const push = useRoutePush();
    const [pathname, setLocation] = useLocation();
    const search = useSearch();
    const listItemClassName = asList ? '' : null;

    // Modals

    const [deleteModalOpened, setDeleteModalOpened] = useState(false);
    const [duplicateModalOpened, setDuplicateModalOpened] = useState(false);

    const onDeleteThemeOpenModal = useCallback(
        (e) => {
            e.preventDefault();
            setDeleteModalOpened(true);
            setDuplicateModalOpened(false);
        },
        [setDeleteModalOpened, setDuplicateModalOpened],
    );

    const onDeleteThemeComplete = useCallback(() => {
        setDeleteModalOpened(false);
        setDuplicateModalOpened(false);
        setLocation(`${pathname}?page=1&last=${Math.random()}`);
    }, [pathname, setLocation, setDeleteModalOpened]);

    const onDeleteThemeCancelled = useCallback(() => {
        setDeleteModalOpened(false);
        setDuplicateModalOpened(false);
    }, [pathname, search, setDeleteModalOpened]);

    const onDuplicateThemeOpenModal = useCallback(
        (e) => {
            e.preventDefault();
            setDuplicateModalOpened(true);
            setDeleteModalOpened(false);
        },
        [setDuplicateModalOpened, setDeleteModalOpened],
    );

    const onDuplicateThemeAction = useCallback(
        (e) => {
            setDuplicateModalOpened(false);
            setDeleteModalOpened(false);
            if (e && e.id) {
                push('themes.show', { theme: e.id });
            }
        },
        [pathname, search, push, setDuplicateModalOpened],
    );

    const finalItems = useMemo(() => {
        const subMenu = [
            withScreens
                ? {
                      id: 'screens',
                      href: url('themes.show', {
                          theme: theme.id,
                      }),
                      label: <FormattedMessage defaultMessage="Screens" description="Menu label" />,
                      className: listItemClassName,
                  }
                : null,
            {
                id: 'settings',
                href: url('themes.settings', {
                    theme: theme.id,
                }),
                label: <FormattedMessage defaultMessage="Settings" description="Menu label" />,
                className: listItemClassName,
            },
            withEditor
                ? {
                      id: 'editor',
                      href: url('themes.editor', {
                          theme: theme.id,
                      }),
                      label: <FormattedMessage defaultMessage="Editor" description="Menu label" />,
                      className: listItemClassName,
                  }
                : null,
            withDuplicate
                ? {
                      id: 'duplicate',
                      href: null,
                      onClick: onDuplicateThemeOpenModal,
                      label: (
                          <FormattedMessage defaultMessage="Duplicate" description="Menu label" />
                      ),
                      className: asList ? listItemClassName : null,
                  }
                : null,
            withDelete
                ? {
                      id: 'delete',
                      href: null,
                      onClick: onDeleteThemeOpenModal,
                      label: <FormattedMessage defaultMessage="Delete" description="Menu label" />,
                      className: asList ? `${listItemClassName} list-group-item-danger` : null,
                  }
                : null,
        ]
            .filter((it) => it !== null)
            .map((it) =>
                it.href === pathname
                    ? {
                          ...it,
                          active: true,
                      }
                    : it,
            );
        return withoutDropdown || asList || asLine
            ? subMenu
            : [
                  {
                      id: 'theme',
                      href: url('themes.show', {
                          theme: theme.id,
                      }),
                      label: theme.title,
                      dropdown: subMenu,
                  },
              ];
    }, [theme, url, withoutDropdown, withDelete, asList, asLine]);

    return (
        <>
            <Menu
                {...props}
                items={finalItems}
                asList={asList}
                asLine={asLine}
                asDropdown={asDropdown}
                flush={flush}
                dropdownAlign={dropdownAlign}
                className={className}
                itemClassName={itemClassName}
                linkClassName={linkClassName}
            />
            {deleteModalOpened ? (
                <ThemeDeleteModal
                    theme={theme}
                    onConfirm={onDeleteThemeComplete}
                    onCancel={onDeleteThemeCancelled}
                />
            ) : null}
            {duplicateModalOpened ? (
                <ThemeDuplicateModal
                    theme={theme}
                    onConfirm={onDuplicateThemeAction}
                    onCancel={onDuplicateThemeAction}
                />
            ) : null}
        </>
    );
};

ThemeMenu.propTypes = propTypes;
ThemeMenu.defaultProps = defaultProps;

export default ThemeMenu;
